import { App } from '../admin.js';
import { MonedaService } from '../services/moneda-service.js';

/**
 * StatsModal - Componente reutilizable para estadisticas con filtros de fecha
 *
 * Usado por: Representantes, Puntos de Venta, Cajas, Operadores
 * Permite filtrar por: hoy, ayer, semana, mes, ultimo corte, rango personalizado
 */

export const StatsModal = {
    // Estado actual
    _entityType: null, // 'representante', 'punto_venta', 'caja', 'operador'
    _entityId: null,
    _currentPeriod: 'hoy',
    _rangoDesde: null,
    _rangoHasta: null,

    // Callback para obtener datos (se setea al abrir el modal)
    _fetchCallback: null,
    _formatCallback: null,

    /**
     * Abre el modal de estadisticas para una entidad
     * @param {Object} options - Opciones de configuracion
     * @param {string} options.title - Titulo del modal
     * @param {string} options.entityType - Tipo de entidad (representante, caja, etc)
     * @param {number} options.entityId - ID de la entidad
     * @param {Function} options.fetchStats - Funcion async que recibe (entityId, desde, hasta) y retorna stats
     * @param {Function} options.formatStats - Funcion que recibe stats y retorna string formateado
     */
    async open(options) {
        const { title, entityType, entityId, fetchStats, formatStats } = options;

        this._entityType = entityType;
        this._entityId = entityId;
        this._fetchCallback = fetchStats;
        this._formatCallback = formatStats;
        this._currentPeriod = 'hoy';

        // Guardar metadata en el modal
        document.getElementById('statsEntityType').value = entityType;
        document.getElementById('statsEntityId').value = entityId;

        // Configurar titulo
        document.getElementById('modalStatsTitle').textContent = title;

        // Resetear UI
        this._resetUI();

        // Abrir modal
        App.openModal('modalStats');

        // Cargar datos con periodo por defecto (hoy)
        await this.refresh();
    },

    /**
     * Resetea la UI del modal a estado inicial
     */
    _resetUI() {
        // Resetear botones de periodo
        const buttons = document.querySelectorAll('.stats-date-btn');
        buttons.forEach(btn => btn.classList.remove('active'));
        const hoyBtn = document.querySelector('.stats-date-btn[data-period="hoy"]');
        if (hoyBtn) hoyBtn.classList.add('active');

        // Ocultar rango personalizado
        document.getElementById('statsRangoInputs').style.display = 'none';

        // Limpiar contenido
        document.getElementById('modalStatsContent').textContent = '';
    },

    /**
     * Cambia el periodo y recarga datos
     * @param {string} period - hoy, ayer, semana, mes, ultimo_corte
     */
    async setPeriod(period) {
        if (period === 'rango') {
            this.toggleRangoInputs();
            return;
        }

        this._currentPeriod = period;

        // Actualizar UI de botones
        const buttons = document.querySelectorAll('.stats-date-btn');
        buttons.forEach(btn => btn.classList.remove('active'));
        const activeBtn = document.querySelector(`.stats-date-btn[data-period="${period}"]`);
        if (activeBtn) activeBtn.classList.add('active');

        // Ocultar rango inputs si estaban visibles
        document.getElementById('statsRangoInputs').style.display = 'none';

        // Calcular fechas segun periodo
        const { desde, hasta } = this._calculateDateRange(period);
        this._rangoDesde = desde;
        this._rangoHasta = hasta;

        // Recargar datos
        await this.refresh();
    },

    /**
     * Muestra/oculta los inputs de rango personalizado
     */
    toggleRangoInputs() {
        const rangoInputs = document.getElementById('statsRangoInputs');
        const isVisible = rangoInputs.style.display !== 'none';

        if (isVisible) {
            rangoInputs.style.display = 'none';
        } else {
            rangoInputs.style.display = 'block';
            // Precargar con fechas actuales si no hay
            const desdeInput = document.getElementById('statsDesde');
            const hastaInput = document.getElementById('statsHasta');
            if (!desdeInput.value) {
                const today = new Date().toISOString().split('T')[0];
                desdeInput.value = today;
                hastaInput.value = today;
            }
        }
    },

    /**
     * Aplica el rango personalizado y recarga datos
     */
    async applyRango() {
        const desde = document.getElementById('statsDesde').value;
        const hasta = document.getElementById('statsHasta').value;

        if (!desde || !hasta) {
            App.toast('Selecciona ambas fechas', 'error');
            return;
        }

        if (desde > hasta) {
            App.toast('La fecha "desde" debe ser anterior a "hasta"', 'error');
            return;
        }

        this._currentPeriod = 'rango';
        this._rangoDesde = desde;
        this._rangoHasta = hasta;

        // Actualizar UI
        const buttons = document.querySelectorAll('.stats-date-btn');
        buttons.forEach(btn => btn.classList.remove('active'));
        const rangoBtn = document.querySelector('.stats-date-btn[data-period="rango"]');
        if (rangoBtn) rangoBtn.classList.add('active');

        await this.refresh();
    },

    /**
     * Calcula rango de fechas segun periodo
     * @param {string} period - Nombre del periodo
     * @returns {{desde: string, hasta: string}} - Fechas en formato YYYY-MM-DD
     */
    _calculateDateRange(period) {
        const today = new Date();
        const formatDate = d => d.toISOString().split('T')[0];

        switch (period) {
            case 'hoy':
                return {
                    desde: formatDate(today),
                    hasta: formatDate(today),
                };

            case 'ayer': {
                const ayer = new Date(today);
                ayer.setDate(ayer.getDate() - 1);
                return {
                    desde: formatDate(ayer),
                    hasta: formatDate(ayer),
                };
            }

            case 'semana': {
                const inicioSemana = new Date(today);
                const dayOfWeek = today.getDay();
                const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Lunes como inicio
                inicioSemana.setDate(today.getDate() - diff);
                return {
                    desde: formatDate(inicioSemana),
                    hasta: formatDate(today),
                };
            }

            case 'mes': {
                const inicioMes = new Date(today.getFullYear(), today.getMonth(), 1);
                return {
                    desde: formatDate(inicioMes),
                    hasta: formatDate(today),
                };
            }

            case 'ultimo_corte':
                // Para ultimo_corte, enviamos null y el backend calcula desde el ultimo corte
                return {
                    desde: null,
                    hasta: null,
                };

            default:
                return {
                    desde: formatDate(today),
                    hasta: formatDate(today),
                };
        }
    },

    /**
     * Recarga los datos con el periodo actual
     */
    async refresh() {
        if (!this._fetchCallback || !this._entityId) {
            console.error('[StatsModal] No hay callback o entityId configurado');
            return;
        }

        const loading = document.getElementById('statsLoading');
        const content = document.getElementById('modalStatsContent');

        // Mostrar loading
        loading.style.display = 'block';
        content.style.display = 'none';

        try {
            // Obtener datos
            const stats = await this._fetchCallback(
                this._entityId,
                this._rangoDesde,
                this._rangoHasta,
                this._currentPeriod
            );

            // Formatear y mostrar
            if (this._formatCallback && stats) {
                const formatted = this._formatCallback(stats, this._currentPeriod, this._rangoDesde, this._rangoHasta);
                content.textContent = formatted;
            } else {
                content.textContent = JSON.stringify(stats, null, 2);
            }
        } catch (error) {
            console.error('[StatsModal] Error fetching stats:', error);
            content.textContent = 'Error al cargar estadisticas';
            App.toast('Error al cargar estadisticas', 'error');
        } finally {
            loading.style.display = 'none';
            content.style.display = 'block';
        }
    },

    formatMoney(cents) {
        return MonedaService.format(cents);
    },

    /**
     * Helper para obtener label del periodo
     * @param {string} period - Codigo del periodo
     * @param {string} desde - Fecha desde (para rango)
     * @param {string} hasta - Fecha hasta (para rango)
     * @returns {string} - Label legible
     */
    getPeriodLabel(period, desde, hasta) {
        const labels = {
            hoy: 'Hoy',
            ayer: 'Ayer',
            semana: 'Esta semana',
            mes: 'Este mes',
            ultimo_corte: 'Desde ultimo corte',
        };

        if (period === 'rango' && desde && hasta) {
            if (desde === hasta) {
                return this._formatDateLabel(desde);
            }
            return `${this._formatDateLabel(desde)} - ${this._formatDateLabel(hasta)}`;
        }

        return labels[period] || period;
    },

    /**
     * Formatea una fecha para mostrar
     * @param {string} dateStr - Fecha en formato YYYY-MM-DD
     * @returns {string} - Fecha formateada
     */
    _formatDateLabel(dateStr) {
        if (!dateStr) return '';
        const date = new Date(dateStr + 'T12:00:00'); // Evitar problemas de timezone
        // TODO Fase 23: locale del nodo, no de brand peruana hardcoded.
        return date.toLocaleDateString('es-PE', {
            day: 'numeric',
            month: 'short',
        });
    },
};
